import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ReferenceDataValue } from 'entities/document';

interface TablePickerGridProps {
  references: ReferenceDataValue[];
  onConfirm: (reference: ReferenceDataValue) => void;
  search?: string;
}

export const TablePickerGrid = ({ references, onConfirm, search = '' }: TablePickerGridProps) => {
  const { t, i18n } = useTranslation('table-import');

  const visibleReferences = useMemo(() => {
    const collator = new Intl.Collator(i18n.language, { numeric: true, sensitivity: 'base' });
    const query = search.trim().toLocaleLowerCase(i18n.language);
    const matches = (reference: ReferenceDataValue) => !query || (reference.name ?? '').toLocaleLowerCase(i18n.language).includes(query);

    return references.filter(matches).sort((a, b) => {
      const importable = Number(!!b.selectionContent?.length) - Number(!!a.selectionContent?.length);
      return importable !== 0 ? importable : collator.compare(a.name ?? '', b.name ?? '');
    });
  }, [references, i18n.language, search]);

  if (references.length === 0) {
    return <h3 className="text-center import-picker-empty">{t('no-data')}</h3>;
  }

  return (
    <>
      {visibleReferences.length === 0 ? (
        <h3 className="text-center import-picker-empty">{t('no-matches')}</h3>
      ) : (
        <div className="table-picker-list">
          <table>
            <tbody>
              {visibleReferences.map((reference) => {
                const selectable = !!reference.selectionContent?.length;

                return (
                  <tr key={reference.revisionId} className={selectable ? '' : 'unselectable-table'}>
                    <td title={reference.name}>{reference.name}</td>
                    <td className="align-right">
                      <button className="btn btn-primary" disabled={!selectable} onClick={() => onConfirm(reference)}>
                        {t('select')}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};
